import React from 'react';
import styled, { css } from 'styled-components';
import { width } from 'styled-system';

import useColors from 'hooks/use-colors';
import useTranslation from 'hooks/use-translation';

import { Link, Tab, Tabs } from 'components/core';
import SearchIcon from 'assets/search-icon';
import Search from 'src/dispensary/components/dispensary-header/search';

export const PRODUCTS = 'products';
export const CATEGORIES = 'categories';
export const BRANDS = 'brands';
export const SPECIALS = 'specials';
export const INFO = 'info';
export const SEARCH = 'search';

export default function DispensarySubheader({
  shouldStick,
  selectedTab,
  onSelectTab,
  infoHref,
  specialsHref,
  brandsHref,
  categoriesHref,
  onToggleSearchInput,
  showSearch,
  hasSpecials,
  subheaderRef,
  hasBrands,
}) {
  const colors = useColors();
  const { t } = useTranslation();
  const hasMissingTab = !hasSpecials || !hasBrands;

  return (
    <StickySubheader fixed={shouldStick || showSearch} ref={subheaderRef}>
      {!showSearch ? (
        <StyledTabs value={selectedTab} onChange={onSelectTab} hasMissingTab={hasMissingTab}>
          <StyledTab
            width={70}
            value={CATEGORIES}
            label={<Link href={categoriesHref}>{t('subheader.categories', CATEGORIES)}</Link>}
          />
          {hasBrands && (
            <StyledTab
              width={45}
              value={BRANDS}
              label={<Link href={brandsHref}>{t('subheader.brands', BRANDS)}</Link>}
            />
          )}
          {hasSpecials && (
            <StyledTab
              width={53}
              value={SPECIALS}
              label={<Link href={specialsHref}>{t('subheader.specials', SPECIALS)}</Link>}
            />
          )}
          <StyledTab width={25} value={INFO} label={<Link href={infoHref}>{t('subheader.info', INFO)}</Link>} />
          <FakeTab width={20} onClick={onToggleSearchInput} tabIndex={0}>
            <SearchIcon width={18} height={18} fill={colors.grey[45]} />
          </FakeTab>
        </StyledTabs>
      ) : (
        <SearchInputContainer>
          <Search alwaysDisplay onClear={onToggleSearchInput} />
          <CancelButton onClick={onToggleSearchInput}>{t('subheader.cancel', 'Cancel')}</CancelButton>
        </SearchInputContainer>
      )}
    </StickySubheader>
  );
}

const StickySubheader = styled.div`
  top: inherit;
  z-index: 200;
  position: relative;
  width: 100%;
  height: 55px;
  border-top: ${({ theme }) => `1px solid ${theme.colors.grey[85]}`};
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.grey[85]}`};
  box-shadow: 0px 2px 0px rgba(0, 0, 0, 0.0408458);
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;

  ${({ fixed }) =>
    fixed &&
    css`
      top: 59px;
      position: fixed;
      border-top: none;
    `};
`;

const StyledTabs = styled(Tabs)`
  width: 100%;
  padding-left: 26px;
  padding-right: 26px;
  border-bottom: none !important;
  box-shadow: none;
  height: 100%;

  .MuiTabs-flexContainer {
    justify-content: space-between;

    ${({ theme }) => theme.breakpoints.down('sm')} {
      justify-content: ${({ hasMissingTab }) => (hasMissingTab ? `space-evenly` : `space-between`)};
    }
  }
`;

const StyledTab = styled(Tab)`
  ${width}

  &.MuiTab-root {
    text-transform: capitalize;
    margin: 0;
    color: ${({ theme }) => theme.colors.grey[45]};
    padding-top: 16px;

    .MuiTab-wrapper {
      font-size: 14px;
      font-weight: bold;
    }
  }
`;

const CancelButton = styled.div`
  height: 100%;
  margin-left: 11px;
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 21px;
  width: 100%;
  margin-bottom: ${({ fixed }) => (fixed ? `10px` : 0)};
`;

const FakeTab = styled.div`
  ${width}
  color: ${({ theme }) => theme.colors.grey[45]};
  padding-top: 16px;
  svg {
    height: 18px;
    width: 18px;
  }
`;
